import React, {useEffect, useState} from "react";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import {useFormik} from "formik";
import {Button, Col, Form, Modal, Popconfirm, Row, Select, Switch} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../../hooks/useStore";
import {HideableInput} from "../../../../_defaultComponents/HideableInput/HideableInput";
import TagInput from "../../../../_defaultComponents/TagInput/TagInput";
export const IntegrationUpdateModal = ({
    subcompanyId,
    selectedIntegration,
    visibility,
    onCancel,
    integrationsFields = [],
    paymentTypes = [],
    paymentTypeRules = {}
}) => {
    const {t} = useTranslation(["integrations", "paymentTypes"]);
    const stores = useStore();
    const [initialValues, setInitialValues] = useState({});
    const [validation, setValidation] = useState(
        yup.object().shape({
            integrationType: yup
                .boolean()
                .required(t("integrations.validations.platformRequired", {ns: "integrations"}))
        })
    );
    const [confirmLoading, setConfirmLoading] = useState();
    const [tags, setTags] = useState([]);

    const formik = useFormik({
        initialValues: {
            isActive: Boolean(selectedIntegration?.isActive),
            canCreateOrder: Boolean(selectedIntegration?.canCreateOrder),
            canRunCallbacks: Boolean(selectedIntegration?.canRunCallbacks),
            integrationType: "",
            supportedPaymentTypes: selectedIntegration?.supportedPaymentTypes || [],
            allowedOrderProviders: selectedIntegration?.allowedOrderProviders || [],
            _id: selectedIntegration?.isMultiple && selectedIntegration?._id,
            ...initialValues
        },
        validationSchema: validation,
        enableReinitialize: true,
        onSubmit: async values => {
            setConfirmLoading(true);
            try {
                const subCompanyId = subcompanyId;
                const {integrationType, ...integration} = values;

                integration.supportedPaymentTypes = integration.supportedPaymentTypes.filter(
                    paymentType =>
                        !paymentTypeRules[integrationType]?.disallowedTypes?.includes(paymentType)
                );

                await stores.companyStore.updateSubcompanyIntegration({
                    subCompanyId,
                    integrationType,
                    integration
                });

                onCancel(true);
            } catch (error) {
                Sentry.captureException(error);
            } finally {
                setConfirmLoading(false);
            }
        }
    });

    const {values, errors, touched, handleSubmit, setFieldValue, resetForm} = formik;

    const generateInitialValuesByType = () => {
        const initialValues = {integrationType: selectedIntegration?.type};
        integrationsFields.forEach(integration => {
            if (integration.type === selectedIntegration?.type) {
                integration.exclusiveFields.forEach(exclusiveField => {
                    initialValues[exclusiveField] = selectedIntegration?.[exclusiveField] || "";
                });
            }
        });
        initialValues.supportedPaymentTypes = selectedIntegration?.supportedPaymentTypes || [];
        initialValues.allowedOrderProviders = selectedIntegration?.allowedOrderProviders || [];
        setTags(initialValues.allowedOrderProviders);
        return initialValues;
    };

    const generateValidationByType = () => {
        const validation = {};
        integrationsFields.forEach(integration => {
            if (integration.type === selectedIntegration?.type) {
                integration.exclusiveFields.forEach(exclusiveField => {
                    validation[exclusiveField] = yup
                        .string()
                        .required(t("validation.required", {ns: "common"}));
                });
            }
        });
        return yup.object().shape(validation);
    };

    const reset = () => {
        setInitialValues({});
        resetForm();
        setValidation(
            yup.object().shape({
                integrationType: yup
                    .boolean()
                    .required(t("integrations.validations.platformRequired", {ns: "integrations"}))
            })
        );
    };

    const handleChangeFlags = (field, checked) => {
        switch (field) {
            case "isActive":
                handleIsActiveChange(checked);
                break;
            case "canCreateOrder":
                handleCanCreateOrderChange(checked);
                break;
            case "canRunCallbacks":
                handleCanRunCallbacksChange(checked);
                break;
        }
    };

    const handleIsActiveChange = checked => {
        setFieldValue("isActive", checked);
        if (!checked) {
            setFieldValue("canCreateOrder", false);
            setFieldValue("canRunCallbacks", false);
        }
    };
    const handleCanCreateOrderChange = checked => {
        setFieldValue("canCreateOrder", checked);
        if (checked) {
            setFieldValue("isActive", true);
        } else {
            setFieldValue("canRunCallbacks", false);
        }
    };
    const handleCanRunCallbacksChange = checked => {
        setFieldValue("canRunCallbacks", checked);
        if (checked) {
            setFieldValue("isActive", true);
            setFieldValue("canCreateOrder", true);
        }
    };
    const handleRemove = async () => {
        setConfirmLoading(true);
        try {
            const subCompanyId = subcompanyId;
            const {integrationType} = values;

            await stores.companyStore.deleteSubcompanyIntegration({
                subCompanyId,
                integrationType,
                integrationId: selectedIntegration?.isMultiple && selectedIntegration?._id
            });
            onCancel(true);
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            setConfirmLoading(false);
        }
    };
    useEffect(() => reset(), [visibility]);
    useEffect(() => {
        if (selectedIntegration?.type) {
            setInitialValues(generateInitialValuesByType());
            setValidation(generateValidationByType());
        }
    }, [selectedIntegration?.type]);

    useEffect(() => {
        setFieldValue("allowedOrderProviders", tags);
    }, [tags]);

    if (!selectedIntegration?.type) return null;
    return (
        <Modal
            title={t("integrations.modals.updateModal.title", {
                ns: "integrations",
                platform: t(`integrations.platforms.${selectedIntegration?.type}.name`, {
                    ns: "integrations"
                })
            })}
            visible={visibility}
            onCancel={() => {
                onCancel(false);
            }}
            footer={
                <Row justify="space-between">
                    <Row justify="start">
                        <Col>
                            <Popconfirm
                                title={t("modal.confirmTitle", {ns: "common"})}
                                open={open}
                                onConfirm={handleRemove}
                                onCancel={() => setOpen(false)}
                                okText={t("modal.yesButton", {ns: "common"})}
                                cancelText={t("modal.noButton", {ns: "common"})}
                                disabled={confirmLoading || !values.integrationType}
                            >
                                <Button
                                    type="danger"
                                    loading={confirmLoading}
                                    disabled={confirmLoading || !values.integrationType}
                                >
                                    {t("actions.delete", {ns: "common"})}
                                </Button>
                            </Popconfirm>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Col>
                            <Button
                                onClick={() => {
                                    onCancel(false);
                                }}
                                loading={confirmLoading}
                            >
                                {t("modal.cancelButton2", {ns: "common"})}
                            </Button>
                            <Popconfirm
                                title={t("modal.confirmTitle", {ns: "common"})}
                                open={open}
                                onConfirm={handleSubmit}
                                onCancel={() => setOpen(false)}
                                okText={t("modal.yesButton", {ns: "common"})}
                                cancelText={t("modal.noButton", {ns: "common"})}
                                disabled={!values.integrationType}
                            >
                                <Button
                                    type="primary"
                                    disabled={!values.integrationType}
                                    loading={confirmLoading}
                                >
                                    {t("actions.update", {ns: "common"})}
                                </Button>
                            </Popconfirm>
                        </Col>
                    </Row>
                </Row>
            }
        >
            <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                <Row>
                    {integrationsFields.map(
                        integration =>
                            integration.type === values.integrationType &&
                            integration.exclusiveFields.map(exclusiveField => (
                                <Col span={24} key={`${integration.type}.${exclusiveField}`}>
                                    <Form.Item
                                        validateStatus={
                                            errors[exclusiveField] && touched[exclusiveField]
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            errors[exclusiveField] && touched[exclusiveField]
                                                ? errors[exclusiveField]
                                                : null
                                        }
                                    >
                                        <h4>
                                            {t(
                                                `integrations.platforms.${integration.type}.fields.${exclusiveField}`,
                                                {
                                                    ns: "integrations"
                                                }
                                            )}
                                        </h4>
                                        <HideableInput
                                            defaultHidden={true}
                                            value={values[exclusiveField]}
                                            onChange={value => {
                                                setFieldValue(exclusiveField, value);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            ))
                    )}
                    <Col span={24}>
                        <Form.Item
                            validateStatus={
                                errors.supportedPaymentTypes && touched.supportedPaymentTypes
                                    ? "error"
                                    : "success"
                            }
                            help={
                                errors.supportedPaymentTypes && touched.supportedPaymentTypes
                                    ? errors.supportedPaymentTypes
                                    : null
                            }
                        >
                            <h4>
                                {t(`integrations.commonFields.supportedPaymentTypes`, {
                                    ns: "integrations"
                                })}
                            </h4>
                            <Select
                                name="supportedPaymentTypes"
                                data-testid="supportedPaymentTypes"
                                value={values.supportedPaymentTypes}
                                placeholder={t(
                                    "integrations.commonFields.supportedPaymentTypesPlaceholder",
                                    {
                                        ns: "integrations"
                                    }
                                )}
                                mode="multiple"
                                optionFilterProp="label"
                                onChange={value => setFieldValue("supportedPaymentTypes", value)}
                            >
                                {paymentTypes
                                    .filter(
                                        paymentType =>
                                            !(
                                                paymentTypeRules[
                                                    values.integrationType
                                                ]?.disallowedTypes?.includes(paymentType) &&
                                                !selectedIntegration?.supportedPaymentTypes?.includes(
                                                    paymentType
                                                )
                                            )
                                    )
                                    .map(paymentType => (
                                        <Select.Option
                                            value={paymentType}
                                            key={paymentType}
                                            label={t(`${paymentType}`, {ns: "paymentTypes"})}
                                        >
                                            {t(`${paymentType}`, {ns: "paymentTypes"})}
                                        </Select.Option>
                                    ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            validateStatus={
                                errors.allowedOrderProviders && touched.allowedOrderProviders
                                    ? "error"
                                    : "success"
                            }
                            help={
                                errors.allowedOrderProviders && touched.allowedOrderProviders
                                    ? errors.allowedOrderProviders
                                    : null
                            }
                        >
                            <h4>
                                {t(`integrations.commonFields.allowedOrderProviders`, {
                                    ns: "integrations"
                                })}
                            </h4>
                            <TagInput
                                tags={tags}
                                setTags={setTags}
                                addTitle={t(`integrations.commonFields.addAllowedOrderProvider`, {
                                    ns: "integrations"
                                })}
                                addPlaceHolderText={t(
                                    `integrations.commonFields.allowedOrderProvidersPlaceholder`,
                                    {
                                        ns: "integrations"
                                    }
                                )}
                            ></TagInput>
                        </Form.Item>
                    </Col>
                    {["isActive", "canCreateOrder", "canRunCallbacks"].map(field => (
                        <Col span={24} key={field}>
                            <Form.Item
                                validateStatus={
                                    errors[field] && touched[field] ? "error" : "success"
                                }
                                help={errors[field] && touched[field] ? errors[field] : null}
                            >
                                <h4>
                                    {" "}
                                    {t(`integrations.commonFields.${field}`, {
                                        ns: "integrations"
                                    })}
                                </h4>
                                <Switch
                                    checked={values[field]}
                                    checkedChildren={t("other.active", {ns: "common"})}
                                    unCheckedChildren={t("other.passive", {ns: "common"})}
                                    data-testid={field}
                                    onChange={checked => handleChangeFlags(field, checked)}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </Form>
        </Modal>
    );
};
